import { create } from "zustand";
import type {
  Catalogo,
  Cotizacion,
  Pieza,
  Producto,
  Sitio,
} from "../domain/types";
import { preciosVigentes, type CambioPrecio } from "../domain/preciosVigentes";
import { nuevaCotizacion } from "../domain/model";
import {
  cotizacionesPropuestas,
  letraPropuesta,
  snapshotPropuesta,
  type Propuesta,
} from "../domain/propuestas";
interface Editor {
  cotizacion: Cotizacion;
  propuestas: Propuesta[];
  siguientePropuesta: number;
  agregarPropuesta: () => void;
  cambiarPropuesta: (numero: string) => void;
  quitarPropuesta: (numero: string) => void;
  todas: () => Cotizacion[];
  guardarRegistro: (numero: string, guardado: Guardado) => void;
  marcarGuardada: () => void;
  clavesPropuestas: Record<string, { fingerprint: string; key: string }>;
  dirty: boolean;
  tareasActivas: number;
  operacionGuardado: { fingerprint: string; key: string } | null;
  claveGuardado: (fingerprint: string, numero?: string) => string;
  tarea: (delta: number) => void;
  patch: (c: Partial<Cotizacion>) => void;
  sitio: (id: string, patch: Partial<Sitio>) => void;
  producto: (sitioId: string, id: string, patch: Partial<Producto>) => void;
  pieza: (
    sitioId: string,
    productoId: string,
    id: string,
    patch: Partial<Pieza>,
  ) => void;
  cargar: (c: Cotizacion) => void;
  // Las piezas se marcan en rojo con sus errores recién después de intentar
  // pasar al paso 3 (no apenas se crean: molestaba ver errores de algo que
  // todavía no se terminó de llenar). Cada `cargar` lo reinicia.
  revisarPiezas: boolean;
  pedirRevision: () => void;
  // Pieza a la que llevar al vendedor (clic en una fila de la vista PDF):
  // App pasa al paso 2, su sitio y su producto se abren y la tarjeta se
  // despliega, se centra y se resalta; la tarjeta lo limpia.
  irAPieza: string | null;
  enfocarPieza: (id: string | null) => void;
  // Sube en cada `cargar`: App vuelve a alinear precios con el catálogo.
  cargas: number;
  preciosActualizados: CambioPrecio[];
  aplicarPreciosVigentes: (catalogo: Catalogo) => void;
  guardada: (guardado: Guardado) => void;
}
// Lo que devuelve el backend al guardar. Una revisión (-R1…) llega con un
// id nuevo: la fila anterior queda Reemplazada y ya no se edita.
export interface Guardado {
  id: string;
  revision: number | null;
  numero: string | null;
  numeroBase: string | null;
}
export const useEditor = create<Editor>((set, get) => ({
  cotizacion: nuevaCotizacion(),
  revisarPiezas: false,
  pedirRevision: () => set({ revisarPiezas: true }),
  irAPieza: null,
  enfocarPieza: (id) => set({ irAPieza: id }),
  propuestas: [],
  siguientePropuesta: 0,
  clavesPropuestas: {},
  todas: () => cotizacionesPropuestas(get().cotizacion, get().propuestas),
  agregarPropuesta: () =>
    set((s) => {
      const base = s.cotizacion.numeroBase ?? s.cotizacion.numero;
      const primera = s.propuestas.length === 0;
      const letraActual =
        s.cotizacion.etiquetaPropuesta?.match(/^PROPUESTA ([A-Z]+)$/)?.[1];
      const siguiente = letraActual
        ? [...letraActual].reduce(
            (n, letra) => n * 26 + letra.charCodeAt(0) - 64,
            0,
          )
        : 1;
      const actual = primera
        ? {
            ...s.cotizacion,
            numero: letraActual ? s.cotizacion.numero : `${base}-A`,
            numeroBase: base,
            etiquetaPropuesta: letraActual
              ? s.cotizacion.etiquetaPropuesta
              : "PROPUESTA A",
          }
        : s.cotizacion;
      const anteriores = primera
        ? [snapshotPropuesta(actual)]
        : s.propuestas.map((p) =>
            p.numero === actual.numero ? snapshotPropuesta(actual) : p,
          );
      const indice = primera ? siguiente : s.siguientePropuesta;
      const sitios = structuredClone(actual.sitios);
      for (const sitio of sitios) {
        sitio.id = crypto.randomUUID();
        for (const pr of sitio.productos) {
          pr.id = crypto.randomUUID();
          for (const p of pr.piezas) p.id = crypto.randomUUID();
        }
      }
      const copia = {
        ...actual,
        numero: `${base}-${letraPropuesta(indice)}`,
        numeroOficial: null,
        etiquetaPropuesta: `PROPUESTA ${letraPropuesta(indice)}`,
        registroId: null,
        revision: null,
        sitios,
      };
      return {
        cotizacion: copia,
        propuestas: [...anteriores, snapshotPropuesta(copia)],
        siguientePropuesta: indice + 1,
        dirty: true,
      };
    }),
  cambiarPropuesta: (numero) =>
    set((s) => {
      if (numero === s.cotizacion.numero) return s;
      const propuestas = s.propuestas.map((p) =>
        p.numero === s.cotizacion.numero ? snapshotPropuesta(s.cotizacion) : p,
      );
      const p = propuestas.find((p) => p.numero === numero);
      return p
        ? {
            propuestas,
            cotizacion: {
              ...s.cotizacion,
              numero: p.numero,
              numeroOficial: p.numeroOficial,
              sitios: p.sitios,
              registroId: p.registroId,
              revision: p.revision,
              etiquetaPropuesta: p.etiqueta,
            },
          }
        : s;
    }),
  quitarPropuesta: (numero) =>
    set((s) => {
      if (s.propuestas.length < 2) return s;
      const propuestas = s.propuestas
        .map((p) =>
          p.numero === s.cotizacion.numero
            ? snapshotPropuesta(s.cotizacion)
            : p,
        )
        .filter((p) => p.numero !== numero);
      const actual =
        numero === s.cotizacion.numero
          ? propuestas[0]
          : snapshotPropuesta(s.cotizacion);
      return {
        propuestas,
        cotizacion: {
          ...s.cotizacion,
          numero: actual.numero,
          numeroOficial: actual.numeroOficial,
          sitios: actual.sitios,
          registroId: actual.registroId,
          revision: actual.revision,
          etiquetaPropuesta: actual.etiqueta,
        },
        dirty: true,
      };
    }),
  guardarRegistro: (numero, { id, revision, numero: oficial, numeroBase }) =>
    set((s) => {
      const registro = { registroId: id, revision, numeroOficial: oficial };
      // La base es común a todas las propuestas del grupo.
      const cotizacion = {
        ...s.cotizacion,
        numeroBaseOficial: numeroBase ?? s.cotizacion.numeroBaseOficial,
      };
      return {
        cotizacion:
          cotizacion.numero === numero
            ? { ...cotizacion, ...registro }
            : cotizacion,
        propuestas: s.propuestas.map((p) =>
          p.numero === numero ? { ...p, ...registro } : p,
        ),
      };
    }),
  marcarGuardada: () => set({ dirty: false }),
  dirty: false,
  tareasActivas: 0,
  operacionGuardado: null,
  claveGuardado: (fingerprint, numero) => {
    if (numero) {
      const anterior = get().clavesPropuestas[numero];
      if (anterior?.fingerprint === fingerprint) return anterior.key;
      const key = crypto.randomUUID();
      set((s) => ({
        clavesPropuestas: {
          ...s.clavesPropuestas,
          [numero]: { fingerprint, key },
        },
      }));
      return key;
    }
    const anterior = get().operacionGuardado;
    if (anterior?.fingerprint === fingerprint) return anterior.key;
    const key = crypto.randomUUID();
    set({ operacionGuardado: { fingerprint, key } });
    return key;
  },
  tarea: (delta) =>
    set((s) => ({ tareasActivas: Math.max(0, s.tareasActivas + delta) })),
  patch: (patch) =>
    set((s) => ({ cotizacion: { ...s.cotizacion, ...patch }, dirty: true })),
  sitio: (id, patch) =>
    set((s) => ({
      cotizacion: {
        ...s.cotizacion,
        sitios: s.cotizacion.sitios.map((x) =>
          x.id === id ? { ...x, ...patch } : x,
        ),
      },
      dirty: true,
    })),
  producto: (sitioId, id, patch) =>
    set((s) => ({
      cotizacion: {
        ...s.cotizacion,
        sitios: s.cotizacion.sitios.map((x) =>
          x.id === sitioId
            ? {
                ...x,
                productos: x.productos.map((p) =>
                  p.id === id ? { ...p, ...patch } : p,
                ),
              }
            : x,
        ),
      },
      dirty: true,
    })),
  pieza: (sitioId, productoId, id, patch) =>
    set((s) => ({
      cotizacion: {
        ...s.cotizacion,
        sitios: s.cotizacion.sitios.map((x) =>
          x.id === sitioId
            ? {
                ...x,
                productos: x.productos.map((pr) =>
                  pr.id === productoId
                    ? {
                        ...pr,
                        piezas: pr.piezas.map((p) =>
                          p.id === id ? { ...p, ...patch } : p,
                        ),
                      }
                    : pr,
                ),
              }
            : x,
        ),
      },
      dirty: true,
    })),
  cargas: 0,
  preciosActualizados: [],
  // No marca `dirty`: el total que se valida y guarda ya es el de n8n con el
  // precio vigente; esto solo alinea la vista previa con él.
  aplicarPreciosVigentes: (catalogo) => {
    const { cotizacion, cambios } = preciosVigentes(get().cotizacion, catalogo);
    if (cambios.length)
      set((s) => ({
        cotizacion,
        preciosActualizados: [...s.preciosActualizados, ...cambios],
      }));
  },
  cargar: (cotizacion) =>
    set((s) => ({
      revisarPiezas: false,
      cotizacion,
      cargas: s.cargas + 1,
      preciosActualizados: [],
      dirty: false,
      operacionGuardado: null,
      propuestas: [],
      siguientePropuesta: 0,
      clavesPropuestas: {},
    })),
  guardada: ({ id, revision, numero, numeroBase }) =>
    set((s) => ({
      cotizacion: {
        ...s.cotizacion,
        registroId: id,
        revision,
        numeroOficial: numero,
        numeroBaseOficial: numeroBase,
      },
      dirty: false,
    })),
}));
